import type { GuidelineItem, GuidelineRule, UnfixableDomainRule } from '../types/guideline';
import type { GuidelineDraftCategory, GuidelineDraftMetadata } from '../types/accuracy';
import { DEFAULT_CHUNK_SETTINGS } from './guidelineDraftMetadata';

const quoteYamlString = (value: string) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

const appendYamlScalar = (lines: string[], indent: number, label: string, value?: string | null) => {
  if (value === undefined || value === null) return;
  const s = String(value);
  if (s.trim() === '') return;
  const pad = ' '.repeat(indent);
  if (s.includes('\n')) {
    lines.push(`${pad}${label}: |`);
    s.split('\n').forEach((line) => lines.push(`${pad}  ${line}`));
  } else {
    lines.push(`${pad}${label}: ${quoteYamlString(s)}`);
  }
};

const appendYamlNumberIfPresent = (
  lines: string[],
  indent: number,
  label: string,
  value?: number | null
) => {
  if (value === undefined || value === null) return;
  if (typeof value !== 'number' || Number.isNaN(value)) return;
  lines.push(`${' '.repeat(indent)}${label}: ${value}`);
};

const appendYamlStringArray = (lines: string[], indent: number, label: string, values?: string[] | null) => {
  const pad = ' '.repeat(indent);
  if (!values || values.length === 0) {
    lines.push(`${pad}${label}: []`);
    return;
  }
  lines.push(`${pad}${label}:`);
  values.forEach((value) => lines.push(`${pad}  - ${quoteYamlString(value)}`));
};

const appendYamlImageAssets = (lines: string[], indent: number, label: string, assets?: unknown[] | null) => {
  const pad = ' '.repeat(indent);
  if (!assets || assets.length === 0) {
    lines.push(`${pad}${label}: []`);
    return;
  }
  lines.push(`${pad}${label}:`);
  assets.forEach((asset) => {
    const a = asset as Record<string, unknown>;
    lines.push(`${pad}  -`);
    appendYamlScalar(lines, indent + 4, 'id', String(a?.id ?? ''));
    appendYamlScalar(lines, indent + 4, 'file_path', String(a?.file_path ?? ''));
    appendYamlScalar(lines, indent + 4, 'content_type', String(a?.content_type ?? ''));
    appendYamlScalar(lines, indent + 4, 'original_filename', String(a?.original_filename ?? ''));
  });
};

const formatStringListYaml = (label: string, values: string[]) => {
  if (!values || values.length === 0) return `  ${label}: []`;
  return [`  ${label}:`, ...values.map((value) => `    - "${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`)].join(
    '\n'
  );
};

const formatUnfixableByDomainYaml = (rules: UnfixableDomainRule[]) => {
  if (!rules || rules.length === 0) return '  unfixable_by_domain: []';
  const lines: string[] = ['  unfixable_by_domain:'];
  for (const rule of rules) {
    lines.push(`    - domain: "${rule.domain.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    const desc = rule.description.replace(/\n/g, '\\n');
    lines.push(`      description: "${desc.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    if (rule.asp) lines.push(`      asp: "${rule.asp.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    if (rule.operation_company) {
      lines.push(`      operation_company: "${rule.operation_company.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    }
    if (rule.media_name) {
      lines.push(`      media_name: "${rule.media_name.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
    }
  }
  return lines.join('\n');
};

export function buildMetadataYamlBlock(metadata: GuidelineDraftMetadata): string {
  const cs = metadata.chunk_settings ?? DEFAULT_CHUNK_SETTINGS;
  const lines: string[] = ['metadata:'];
  lines.push(formatStringListYaml('related_asps', metadata.related_asps ?? []));
  lines.push(formatStringListYaml('operation_companies', metadata.operation_companies ?? []));
  lines.push(formatStringListYaml('media_names', metadata.media_names ?? []));
  lines.push('  chunk_settings:');
  lines.push(`    chunk_context_before: ${cs.chunk_context_before ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before}`);
  lines.push(`    chunk_context_after: ${cs.chunk_context_after ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after}`);
  lines.push(`    max_chunk_size: ${cs.max_chunk_size ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size}`);
  lines.push(`    chunk_overlap: ${cs.chunk_overlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap}`);
  lines.push(
    `    keyword_group_distance: ${cs.keyword_group_distance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance}`
  );
  lines.push(formatStringListYaml('keyword_list', metadata.keyword_list ?? []));
  lines.push(`  enable_search: ${metadata.enable_search ?? false}`);
  if (metadata.enable_search && (metadata.search_keywords?.length ?? 0) > 0) {
    lines.push(formatStringListYaml('search_keywords', metadata.search_keywords ?? []));
  }
  lines.push(`  enable_filtering: ${metadata.enable_filtering ?? true}`);
  if ((metadata.other_content_keywords?.length ?? 0) > 0) {
    lines.push(formatStringListYaml('other_content_keywords', metadata.other_content_keywords ?? []));
  }
  if ((metadata.auto_expand_keywords?.length ?? 0) > 0) {
    lines.push(formatStringListYaml('auto_expand_keywords', metadata.auto_expand_keywords ?? []));
  }
  lines.push(`  enable_unfixable_classification: ${metadata.enable_unfixable_classification ?? false}`);
  lines.push(formatUnfixableByDomainYaml(metadata.unfixable_by_domain ?? []));
  return lines.join('\n');
}

export function categoriesToGuidelineItems(categories: GuidelineDraftCategory[]): GuidelineItem[] {
  return categories.map((cat) => ({
    id: cat.id,
    violation_category: cat.violation_category,
    rules: cat.rules as GuidelineRule[],
  }));
}

export function guidelineItemsToYamlBody(items: GuidelineItem[]): string {
  const lines: string[] = [];
  items.forEach((item) => {
    lines.push(`- id: ${quoteYamlString(item.id || '')}`);
    appendYamlScalar(lines, 2, 'violation_category', item.violation_category || '');
    const rules = item.rules && item.rules.length > 0 ? item.rules : [];
    if (rules.length > 0) {
      lines.push('  rules:');
      rules.forEach((rule, ruleIndex) => {
        lines.push('    -');
        appendYamlScalar(lines, 6, 'name', rule.name || `Rule ${ruleIndex + 1}`);
        appendYamlScalar(lines, 6, 'type', rule.type);
        appendYamlScalar(lines, 6, 'patrol_source', rule.patrol_source);
        appendYamlScalar(lines, 6, 'detail', rule.detail);
        appendYamlScalar(lines, 6, 'violation_check_prompt', rule.violation_check_prompt);
        appendYamlScalar(lines, 6, 'existence_check_prompt', rule.existence_check_prompt);

        if (rule.type === 'forbidden_image') {
          appendYamlNumberIfPresent(lines, 6, 'similarity_threshold', (rule as any).similarity_threshold);
          appendYamlNumberIfPresent(lines, 6, 'min_good_matches', (rule as any).min_good_matches);
          appendYamlNumberIfPresent(lines, 6, 'min_score', (rule as any).min_score);
          appendYamlNumberIfPresent(lines, 6, 'min_inlier_ratio', (rule as any).min_inlier_ratio);
          appendYamlImageAssets(lines, 6, 'forbidden_image_assets', (rule as any).forbidden_image_assets);
        }
        if (rule.type === 'required_image_match') {
          appendYamlScalar(lines, 6, 'target_image_extraction_prompt', (rule as any).target_image_extraction_prompt);
          appendYamlNumberIfPresent(lines, 6, 'min_good_matches', (rule as any).min_good_matches);
          appendYamlNumberIfPresent(lines, 6, 'min_score', (rule as any).min_score);
          appendYamlNumberIfPresent(lines, 6, 'min_inlier_ratio', (rule as any).min_inlier_ratio);
          appendYamlImageAssets(lines, 6, 'required_image_assets', (rule as any).required_image_assets);
        }

        if (rule.forbidden_regular_expressions) {
          appendYamlStringArray(lines, 6, 'forbidden_regular_expressions', rule.forbidden_regular_expressions);
        }
        appendYamlScalar(lines, 6, 'forbidden_detect_message', rule.forbidden_detect_message);
        if (rule.musthave_regular_expressions) {
          appendYamlStringArray(lines, 6, 'musthave_regular_expressions', rule.musthave_regular_expressions);
        }
        appendYamlScalar(lines, 6, 'musthave_not_detect_message', rule.musthave_not_detect_message);

        if (rule.regular_expression_conditions && rule.regular_expression_conditions.length > 0) {
          lines.push('      regular_expression_conditions:');
          rule.regular_expression_conditions.forEach((condition) => {
            lines.push('        -');
            appendYamlScalar(lines, 10, 'type', condition.type || 'allow_if');
            if (condition.context_window !== undefined && condition.context_window !== null) {
              lines.push(`          context_window: ${condition.context_window}`);
            }
            appendYamlScalar(lines, 10, 'rule', condition.rule);
          });
        }
      });
    }
    lines.push('');
  });
  return lines.join('\n');
}

const indentYamlBlock = (yamlString: string, spaces: number) => {
  const indent = ' '.repeat(spaces);
  return yamlString
    .split('\n')
    .map((line) => (line.trim().length === 0 ? line : `${indent}${line}`))
    .join('\n');
};

export function buildGuidelineDraftYaml(params: {
  guideline_id: string;
  base_version: number;
  metadata: GuidelineDraftMetadata;
  categories: GuidelineDraftCategory[];
}): string {
  const metadataYaml = buildMetadataYamlBlock(params.metadata);
  const itemsYamlContent = guidelineItemsToYamlBody(categoriesToGuidelineItems(params.categories)).trimEnd();
  const header = `guideline_id: ${quoteYamlString(params.guideline_id)}\nbase_version: ${params.base_version}\n`;
  if (!itemsYamlContent.trim()) {
    return `${header}${metadataYaml}\nitems: []\n`;
  }
  return `${header}${metadataYaml}\nitems:\n${indentYamlBlock(itemsYamlContent, 2)}\n`;
}

function trimText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseStringArray(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.map((s) => trimText(s)).filter(Boolean) : [];
}

function parseUnfixableByDomain(raw: unknown): UnfixableDomainRule[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry: any) => {
      const domain = trimText(entry?.domain);
      const description = trimText(entry?.description);
      if (!domain || !description) return null;
      return {
        id: trimText(entry?.id) || crypto.randomUUID(),
        domain,
        description,
        asp: trimText(entry?.asp) || undefined,
        operation_company: trimText(entry?.operation_company ?? entry?.operationCompany) || undefined,
        media_name: trimText(entry?.media_name ?? entry?.mediaName) || undefined,
        source_row: entry?.source_row ?? entry?.sourceRow ?? null,
      } as UnfixableDomainRule;
    })
    .filter((r): r is UnfixableDomainRule => r != null);
}

export function parseMetadataSection(raw: unknown): GuidelineDraftMetadata | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const m = raw as Record<string, unknown>;
  const cs = (m.chunk_settings ?? m.chunkSettings ?? {}) as Record<string, unknown>;
  return {
    related_asps: parseStringArray(m.related_asps ?? m.relatedAsps),
    operation_companies: parseStringArray(m.operation_companies ?? m.operationCompanies),
    media_names: parseStringArray(m.media_names ?? m.mediaNames),
    chunk_settings: {
      chunk_context_before: Number(cs.chunk_context_before ?? cs.chunkContextBefore ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before),
      chunk_context_after: Number(cs.chunk_context_after ?? cs.chunkContextAfter ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after),
      max_chunk_size: Number(cs.max_chunk_size ?? cs.maxChunkSize ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size),
      chunk_overlap: Number(cs.chunk_overlap ?? cs.chunkOverlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap),
      keyword_group_distance: Number(
        cs.keyword_group_distance ?? cs.keywordGroupDistance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance
      ),
    },
    keyword_list: parseStringArray(m.keyword_list ?? m.keywordList),
    enable_search: Boolean(m.enable_search ?? m.enableSearch ?? false),
    search_keywords: parseStringArray(m.search_keywords ?? m.searchKeywords),
    enable_filtering: m.enable_filtering != null ? Boolean(m.enable_filtering) : m.enableFiltering != null ? Boolean(m.enableFiltering) : true,
    other_content_keywords: parseStringArray(m.other_content_keywords ?? m.titleExtractionKeywords),
    auto_expand_keywords: parseStringArray(m.auto_expand_keywords ?? m.autoExpandKeywords),
    enable_unfixable_classification: Boolean(
      m.enable_unfixable_classification ?? m.enableUnfixableClassification ?? false
    ),
    unfixable_by_domain: parseUnfixableByDomain(m.unfixable_by_domain ?? m.unfixableByDomain),
  };
}

export function parseItemsSection(raw: unknown): GuidelineDraftCategory[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: any, index: number) => ({
    id: trimText(item?.id) || `category_${index + 1}`,
    violation_category: trimText(item?.violation_category ?? item?.violationCategory),
    rules: Array.isArray(item?.rules)
      ? item.rules.map((rule: any, ruleIndex: number) => ({
          ...rule,
          id: trimText(rule?.id) || `rule_${ruleIndex + 1}`,
          type: trimText(rule?.type) || 'text_check',
          name: trimText(rule?.name) || undefined,
        }))
      : [],
  }));
}
